import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";

// ── Design tokens ──────────────────────────────────────────────────────────
const ACCENT   = "#1E90FF";
const ACCENT_2 = "#60B4FF";
const BG       = "#000";
const SURFACE  = "rgba(255,255,255,0.03)";
const BORDER   = "rgba(255,255,255,0.07)";

// ── Blog data (replace with your real data / props) ───────────────────────
const PREVIEW_BLOGS = [
  {
    id: 1,
    image:"/img4.png",
    slug: "build-secure-ai-assistants-with-private-llms",
    category: "Agents",
    date: "May 14, 2025",
    readTime: "7 min",
    title: "Build Secure AI Assistants with Private LLMs",
    excerpt:
      "How Enterprises Can Build Secure AI Assistants with Private LLMs: A Comprehensive Guide to Protecting Data and Ensuring Compliance",
    tags: ["Architecture", "LLMs", "Agents"],
    accent: "#1E90FF",
  },
  {
    id: 2,
    image:"/img3.png",
    slug: "rag-vs-fine-tuning",
    category: "Rag",
    date: "May 20, 2025",
    readTime: "10 min",
    title: "RAG vs Fine-Tuning: The Definitive Trade-off Guide",
    excerpt:
      "Retrieval-augmented generation and model fine-tuning solve different problems. Here's a rigorous framework for choosing between them.",
    tags: ["RAG", "Fine-tuning", "Research"],
    accent: "#3B82F6",
  },
  {
    id: 3,
    image:"/img6.png",
    slug: "the-future-of-enterprise-efficiency",
    category: "Enterprise AI",
    date: "Apr 29, 2025",
    readTime: "5 min",
    title: "The Future of Enterprise Efficiency",
    excerpt:
      "From Manual Workflows to Autonomous Operations: The Future of Enterprise Efficiency",
    tags: ["Enterprise", "Autonomy", "Systems"],
    accent: "#0EA5E9",
  },
];

// ── Utility: stagger fade-up ───────────────────────────────────────────────
function FadeUp({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ── Ambient glow dot ──────────────────────────────────────────────────────
function Glow({ top, left, size = 400, opacity = 0.1 }) {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        top,
        left,
        width: size,
        height: size,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${ACCENT}, transparent 70%)`,
        opacity,
        filter: "blur(70px)",
        pointerEvents: "none",
        transform: "translate(-50%, -50%)",
      }}
    />
  );
}

// ── Individual card ───────────────────────────────────────────────────────
function BlogCard({ blog, index, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <FadeUp delay={0.1 + index * 0.1}>
      <motion.article
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        onClick={() => onClick(blog.slug)}
        animate={{
          y: hovered ? -8 : 0,
          boxShadow: hovered
            ? `0 24px 60px rgba(30,144,255,0.16), 0 0 0 1px rgba(30,144,255,0.18)`
            : `0 0 0 1px ${BORDER}`,
        }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{
          background: SURFACE,
          borderRadius: 20,
          padding: "36px 32px 30px",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          position: "relative",
          overflow: "hidden",
          height: "100%",
          minHeight: 300,
        }}
      >
        {/* Top accent line */}
        <motion.div
          animate={{ scaleX: hovered ? 1 : 0.0001, opacity: hovered ? 1 : 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, ${blog.accent}, transparent)`,
            transformOrigin: "left",
          }}
        />

        {/* Corner glow */}
        <motion.div
          animate={{ opacity: hovered ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${blog.accent}22, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        {/* Index number — DeepMind-inspired editorial marker */}
       <div
  style={{
    width: "100%",
    height: "180px",
    borderRadius: "14px",
    overflow: "hidden",
    marginBottom: "20px",
  }}
>
  <img
    src={blog.image}
    alt={blog.title}
    style={{
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.4s ease",
      transform: hovered ? "scale(1.05)" : "scale(1)",
    }}
  />
</div>

        {/* Category + date row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: ACCENT,
              background: "rgba(30,144,255,0.1)",
              padding: "4px 10px",
              borderRadius: 100,
            }}
          >
            {blog.category}
          </span>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.75rem",
              color: "rgba(255,255,255,0.3)",
            }}
          >
            {blog.date}
          </span>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.75rem",
              color: "rgba(255,255,255,0.25)",
            }}
          >
            · {blog.readTime} read
          </span>
        </div>

        {/* Title */}
        <h3
          style={{
            fontFamily: "'Sora', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(1.05rem, 1.6vw, 1.22rem)",
            lineHeight: 1.3,
            letterSpacing: "-0.015em",
            color: "#fff",
            margin: "0 0 14px",
          }}
        >
          {blog.title}
        </h3>

        {/* Excerpt */}
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "0.88rem",
            lineHeight: 1.75,
            color: "rgba(255,255,255,0.52)",
            margin: "0 0 24px",
            flex: 1,
          }}
        >
          {blog.excerpt}
        </p>

        {/* Tags */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 24 }}>
          {blog.tags.map((tag) => (
            <span
              key={tag}
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.7rem",
                color: "rgba(255,255,255,0.35)",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
                padding: "3px 9px",
                borderRadius: 6,
                letterSpacing: "0.03em",
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* CTA row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            paddingTop: 20,
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.83rem",
              fontWeight: 600,
              color: hovered ? ACCENT_2 : ACCENT,
              transition: "color 0.25s",
            }}
          >
            Read article
          </span>
          <motion.span
            animate={{ x: hovered ? 6 : 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            style={{ color: ACCENT, fontSize: "1rem", display: "inline-flex", lineHeight: 1 }}
          >
            →
          </motion.span>
        </div>
      </motion.article>
    </FadeUp>
  );
}

// ── Main section ──────────────────────────────────────────────────────────
export default function HomeBlogSection() {
  const navigate = useNavigate();
  const [btnHovered, setBtnHovered] = useState(false);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap');

        .hbs-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          align-items: stretch;
        }

        @media (max-width: 1024px) {
          .hbs-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 640px) {
          .hbs-grid { grid-template-columns: 1fr; }
        }

        /* Divider beam animation */
        @keyframes beam-slide {
          0%   { transform: translateX(-100%); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
      `}</style>

      <section
        style={{
          position: "relative",
          background: BG,
          padding: "clamp(80px, 10vw, 140px) 0",
          overflow: "hidden",
        }}
      >
        {/* Ambient glows */}
        <Glow top="10%"  left="15%"  size={500} opacity={0.07} />
        <Glow top="80%"  left="85%"  size={450} opacity={0.07} />
        <Glow top="50%"  left="50%"  size={700} opacity={0.04} />

        {/* Subtle grid texture */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            opacity: 0.018,
            backgroundImage: `
              linear-gradient(rgba(30,144,255,1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(30,144,255,1) 1px, transparent 1px)
            `,
            backgroundSize: "64px 64px",
          }}
        />

        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 clamp(20px, 5vw, 72px)",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* ── Section header ── */}
          <FadeUp delay={0}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 24,
                marginBottom: 56,
                flexWrap: "wrap",
              }}
            >
              {/* Left: label + heading */}
              <div>
                {/* Eyebrow */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 18,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 1,
                      background: `linear-gradient(90deg, ${ACCENT}, transparent)`,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: ACCENT,
                    }}
                  >
                    From the newsroom
                  </span>
                </div>

                {/* Heading — two-line editorial split */}
                <h2
                  style={{
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 800,
                    fontSize: "clamp(2rem, 4.5vw, 3.6rem)",
                    lineHeight: 1.05,
                    letterSpacing: "-0.03em",
                    color: "#fff",
                    margin: 0,
                  }}
                >
                  Thinking out loud
                  <br />
                  <span
                    style={{
                      color: "transparent",
                      WebkitTextStroke: "1px rgba(255,255,255,0.25)",
                    }}
                  >
                    on intelligence.
                  </span>
                </h2>

                {/* Sub */}
                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "clamp(0.88rem, 1.1vw, 1rem)",
                    lineHeight: 1.8,
                    color: "rgba(255,255,255,0.45)",
                    marginTop: 16,
                    maxWidth: 480,
                  }}
                >
                  Research, engineering deep dives, and product thinking from
                  the AnthraSync team.
                </p>
              </div>

              {/* Right: desktop Explore More button */}
              <div style={{ flexShrink: 0 }} className="hbs-desktop-cta">
                <ExploreButton
                  hovered={btnHovered}
                  onHoverStart={() => setBtnHovered(true)}
                  onHoverEnd={() => setBtnHovered(false)}
                  onClick={() => navigate("/blog")}
                />
              </div>
            </div>
          </FadeUp>

          {/* ── Divider beam ── */}
          <FadeUp delay={0.05}>
            <div
              style={{
                position: "relative",
                height: 1,
                background: BORDER,
                marginBottom: 52,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`,
                  animation: "beam-slide 3.5s ease-in-out 0.8s 1 forwards",
                  opacity: 0,
                }}
              />
            </div>
          </FadeUp>

          {/* ── Blog cards ── */}
          <div className="hbs-grid">
            {PREVIEW_BLOGS.map((blog, i) => (
              <BlogCard
                key={blog.id}
                blog={blog}
                index={i}
                onClick={(slug) => navigate(`/blogs/${slug}`)}
              />
            ))}
          </div>

          {/* ── Mobile Explore More button ── */}
          <FadeUp delay={0.35}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 52,
              }}
              className="hbs-mobile-cta"
            >
              <ExploreButton
                hovered={btnHovered}
                onHoverStart={() => setBtnHovered(true)}
                onHoverEnd={() => setBtnHovered(false)}
                onClick={() => navigate("/blog")}
                fullWidth
              />
            </div>
          </FadeUp>
        </div>

        {/* Responsive: hide desktop CTA on mobile, show mobile CTA on desktop */}
        <style>{`
          .hbs-desktop-cta { display: flex; }
          .hbs-mobile-cta  { display: none; }
          @media (max-width: 768px) {
            .hbs-desktop-cta { display: none; }
            .hbs-mobile-cta  { display: flex; }
          }
        `}</style>
      </section>
    </>
  );
}

// ── Explore button (reused in both positions) ─────────────────────────────
function ExploreButton({ onClick, onHoverStart, onHoverEnd, hovered, fullWidth }) {
  return (
    <motion.button
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      onClick={onClick}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.96 }}
      animate={{
        boxShadow: hovered
          ? `0 0 32px rgba(30,144,255,0.35), inset 0 0 20px rgba(30,144,255,0.08)`
          : `0 0 0 rgba(0,0,0,0)`,
      }}
      transition={{ duration: 0.3 }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "14px 32px",
        borderRadius: 14,
        background: "rgba(30,144,255,0.08)",
        border: "1px solid rgba(30,144,255,0.3)",
        color: "#fff",
        fontFamily: "'Inter', sans-serif",
        fontWeight: 600,
        fontSize: "0.9rem",
        letterSpacing: "0.02em",
        cursor: "pointer",
        width: fullWidth ? "100%" : "auto",
        justifyContent: fullWidth ? "center" : "flex-start",
        transition: "background 0.3s, border-color 0.3s",
        ...(hovered
          ? { background: "rgba(30,144,255,0.14)", borderColor: "rgba(30,144,255,0.55)" }
          : {}),
      }}
    >
      Explore all articles
      <motion.span
        animate={{ x: hovered ? 5 : 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        style={{ display: "inline-flex", fontSize: "1.1rem", lineHeight: 1 }}
      >
        →
      </motion.span>
    </motion.button>
  );
}